import { calculateRoi } from './calculator';
import { loadDataBundle } from './data';
import { formatCurrency, formatPercent } from './format';
import { renderChart } from './chart';
import type { ProgramRecord, SalaryBenchmarkRecord, TuitionRecord } from './types';

interface State {
  selectedProgram?: ProgramRecord;
  tuitionBySlug: Map<string, TuitionRecord>;
  benchmarkBySlug: Map<string, SalaryBenchmarkRecord>;
}

function must<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing DOM element: ${selector}`);
  }
  return element;
}

const state: State = {
  tuitionBySlug: new Map(),
  benchmarkBySlug: new Map()
};

const form = must<HTMLFormElement>('#calculator-form');
const programSearch = must<HTMLInputElement>('#program-search');
const programOptions = must<HTMLDataListElement>('#program-options');
const currentSalary = must<HTMLInputElement>('#current-salary');
const targetSalary = must<HTMLInputElement>('#target-salary');
const tuitionCost = must<HTMLInputElement>('#tuition-cost');
const employerReimbursement = must<HTMLInputElement>('#employer-reimbursement');
const timeHorizon = must<HTMLInputElement>('#time-horizon');
const currentAge = must<HTMLInputElement>('#current-age');
const retirementAge = must<HTMLInputElement>('#retirement-age');
const growthRate = must<HTMLInputElement>('#growth-rate');
const useBenchmark = must<HTMLButtonElement>('#use-benchmark');
const programNote = must<HTMLParagraphElement>('#program-note');

const breakEvenEl = must<HTMLParagraphElement>('#break-even');
const roi5yEl = must<HTMLParagraphElement>('#roi-5y');
const roi5yNetEl = must<HTMLParagraphElement>('#roi-5y-net');
const lifetimeDeltaEl = must<HTMLParagraphElement>('#lifetime-delta');
const chartCanvas = must<HTMLCanvasElement>('#roi-chart');

function parseNumber(input: HTMLInputElement): number {
  return Number.parseFloat(input.value);
}

function populatePrograms(programs: ProgramRecord[]): void {
  programOptions.innerHTML = '';
  for (const program of programs) {
    const option = document.createElement('option');
    option.value = program.program_name;
    option.dataset.slug = program.program_slug;
    programOptions.append(option);
  }
}

function getProgramByName(programs: ProgramRecord[], name: string): ProgramRecord | undefined {
  return programs.find((program) => program.program_name.toLowerCase() === name.trim().toLowerCase());
}

function setSelectedProgram(program: ProgramRecord): void {
  state.selectedProgram = program;
  const tuition = state.tuitionBySlug.get(program.program_slug);
  tuitionCost.value = tuition ? tuition.tuition_amount_usd.toString() : '0';

  const benchmark = state.benchmarkBySlug.get(program.program_slug);
  if (benchmark) {
    programNote.textContent = `${benchmark.occupation_title} benchmark median: ${formatCurrency(benchmark.median_wage_usd)} (${benchmark.source_year}).`;
  } else {
    programNote.textContent = 'No benchmark mapping yet for this program.';
  }

  recalculate();
}

function recalculate(): void {
  if (!state.selectedProgram) {
    breakEvenEl.textContent = '-';
    roi5yEl.textContent = '-';
    roi5yNetEl.textContent = '-';
    lifetimeDeltaEl.textContent = '-';
    return;
  }

  try {
    const output = calculateRoi({
      program_slug: state.selectedProgram.program_slug,
      current_salary: parseNumber(currentSalary),
      target_salary: parseNumber(targetSalary),
      tuition_cost: parseNumber(tuitionCost),
      employer_reimbursement: parseNumber(employerReimbursement),
      time_horizon_years: Math.floor(parseNumber(timeHorizon)),
      current_age: Math.floor(parseNumber(currentAge)),
      retirement_age: Math.floor(parseNumber(retirementAge)),
      annual_growth_rate: parseNumber(growthRate)
    });

    if (output.break_even_year === null || output.break_even_month === null) {
      breakEvenEl.textContent = `Beyond ${timeHorizon.value} years`;
    } else if (output.break_even_year === 0) {
      breakEvenEl.textContent = 'Immediate';
    } else {
      breakEvenEl.textContent = `${output.break_even_year} year(s), ${output.break_even_month} month(s)`;
    }

    if (output.roi_5y === null) {
      roi5yEl.textContent = 'Immediate / N/A';
    } else {
      roi5yEl.textContent = formatPercent(output.roi_5y);
    }

    roi5yNetEl.textContent = `Net gain at 5 years: ${formatCurrency(output.roi_5y_net)}`;
    lifetimeDeltaEl.textContent = formatCurrency(output.lifetime_delta);

    renderChart(chartCanvas, output);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Invalid input';
    breakEvenEl.textContent = message;
    roi5yEl.textContent = '-';
    roi5yNetEl.textContent = '-';
    lifetimeDeltaEl.textContent = '-';
  }
}

async function boot(): Promise<void> {
  const bundle = await loadDataBundle();

  for (const tuition of bundle.tuition) {
    state.tuitionBySlug.set(tuition.program_slug, tuition);
  }
  for (const benchmark of bundle.salaryBenchmarks) {
    state.benchmarkBySlug.set(benchmark.program_slug, benchmark);
  }

  populatePrograms(bundle.programs);

  const defaultProgram = bundle.programs[0];
  programSearch.value = defaultProgram.program_name;
  setSelectedProgram(defaultProgram);

  programSearch.addEventListener('change', () => {
    const selected = getProgramByName(bundle.programs, programSearch.value);
    if (selected) {
      setSelectedProgram(selected);
      return;
    }
    programNote.textContent = 'Choose a program listed in BU Online offerings.';
  });

  useBenchmark.addEventListener('click', () => {
    if (!state.selectedProgram) {
      return;
    }
    const benchmark = state.benchmarkBySlug.get(state.selectedProgram.program_slug);
    if (benchmark) {
      targetSalary.value = benchmark.median_wage_usd.toString();
      recalculate();
    }
  });

  form.addEventListener('input', recalculate);
}

void boot();
